import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, TrendingUp, DollarSign, Package, Users, Heart, AlertCircle, Download } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, Legend,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  received: 'bg-blue-100 text-blue-700',
  in_process: 'bg-amber-100 text-amber-700',
  out_for_delivery: 'bg-indigo-100 text-indigo-700',
  delivered: 'bg-green-100 text-green-700',
  substitution_needed: 'bg-red-100 text-red-700',
};

const STATUS_LABELS = {
  received: 'Recibidos',
  in_process: 'En proceso',
  out_for_delivery: 'En entrega',
  delivered: 'Entregados',
  substitution_needed: 'Sustitución',
};

const StatCard = ({ icon: Icon, label, value, sublabel, color = 'text-[#0F2922]', testid }) => (
  <Card className="border-[#E5E3D8]" data-testid={testid}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-[#6B6660] font-medium">{label}</div>
          <div className={`text-3xl font-bold mt-2 ${color}`}>{value}</div>
          {sublabel && <div className="text-sm text-[#6B6660] mt-1">{sublabel}</div>}
        </div>
        <div className="p-2 rounded-lg bg-[#FAF9F6]">
          <Icon className="w-5 h-5 text-[#B85042]" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const Dashboard = ({ authHeader }) => {
  const [data, setData] = useState(null);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [resA, resT] = await Promise.all([
          axios.get(`${API}/admin/analytics`, { headers: { Authorization: `Bearer ${authHeader}` } }),
          axios.get(`${API}/admin/analytics/timeseries?days=30`, { headers: { Authorization: `Bearer ${authHeader}` } }),
        ]);
        if (!cancelled) {
          setData(resA.data);
          setSeries(resT.data.series || []);
        }
      } catch (e) {
        console.error('analytics error', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const t = setInterval(load, 30000); // auto-refresh every 30s
    return () => { cancelled = true; clearInterval(t); };
  }, [authHeader]);

  const handleDownloadCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/orders/export.csv`, {
        headers: { Authorization: `Bearer ${authHeader}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const dt = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `pedidos-jabad-${dt}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('csv export failed', e);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="dashboard-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#B85042]" />
      </div>
    );
  }
  if (!data) {
    return <div className="text-center py-12 text-[#6B6660]" data-testid="dashboard-error">No fue posible cargar las métricas.</div>;
  }

  const revMxn = data.revenue.by_currency.find(r => r.currency === 'MXN') || { total_mxn: 0, count: 0 };
  const revUsd = data.revenue.by_currency.find(r => r.currency === 'USD') || { total_usd: 0, count: 0 };

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* CSV download */}
      <div className="flex justify-end">
        <Button
          onClick={handleDownloadCsv}
          variant="outline"
          className="border-[#E5E3D8] rounded-xl text-[#0F2922]"
          data-testid="btn-download-csv"
        >
          <Download className="w-4 h-4 mr-2" />
          Descargar pedidos CSV
        </Button>
      </div>

      {/* Top stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Package}
          label="Pedidos hoy"
          value={data.orders.today}
          sublabel={`${data.orders.week} esta semana`}
          testid="stat-orders-today"
        />
        <StatCard
          icon={TrendingUp}
          label="Pedidos totales"
          value={data.orders.total}
          sublabel={`${data.orders.month} este mes`}
          testid="stat-orders-total"
        />
        <StatCard
          icon={DollarSign}
          label="Ingresos MXN (sem)"
          value={`$${data.revenue.week_total_mxn.toLocaleString('es-MX')}`}
          sublabel={`${revMxn.count} pedidos MXN`}
          color="text-emerald-700"
          testid="stat-revenue-mxn"
        />
        <StatCard
          icon={DollarSign}
          label="Ingresos USD (sem)"
          value={`$${data.revenue.week_total_usd.toLocaleString('es-MX')}`}
          sublabel={`${revUsd.count} pedidos USD`}
          color="text-emerald-700"
          testid="stat-revenue-usd"
        />
      </div>

      {/* Operational status */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          icon={AlertCircle}
          label="Sustituciones pendientes"
          value={data.pending_substitutions}
          color={data.pending_substitutions > 0 ? 'text-red-600' : 'text-[#0F2922]'}
          testid="stat-pending-subs"
        />
        <StatCard
          icon={Users}
          label="Invitados Shabbat pendientes"
          value={data.shabbat_guests.pending}
          sublabel={`${data.shabbat_guests.approved} aprobados`}
          color={data.shabbat_guests.pending > 0 ? 'text-amber-600' : 'text-[#0F2922]'}
          testid="stat-pending-guests"
        />
        <StatCard
          icon={Heart}
          label="Donaciones"
          value={`$${data.donations.total_mxn.toLocaleString('es-MX')}`}
          sublabel={`${data.donations.count} donantes`}
          color="text-rose-700"
          testid="stat-donations"
        />
      </div>

      {/* Time series chart */}
      <Card className="border-[#E5E3D8]" data-testid="card-timeseries">
        <CardContent className="p-5">
          <h3 className="text-lg font-semibold text-[#0F2922] mb-4">Pedidos e ingresos (últimos 30 días)</h3>
          {series.length === 0 ? (
            <p className="text-sm text-[#6B6660] py-8 text-center">Sin pedidos pagados en los últimos 30 días.</p>
          ) : (
            <>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={series} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E3D8" />
                    <XAxis dataKey="date" stroke="#6B6660" fontSize={11} tickFormatter={(d) => d.slice(5)} />
                    <YAxis yAxisId="left" stroke="#B85042" fontSize={11} />
                    <YAxis yAxisId="right" orientation="right" stroke="#0F2922" fontSize={11} />
                    <Tooltip
                      contentStyle={{ background: '#FAF9F6', border: '1px solid #E5E3D8', borderRadius: '8px' }}
                      labelStyle={{ color: '#0F2922', fontWeight: 'bold' }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }} />
                    <Line yAxisId="left" type="monotone" dataKey="revenue_mxn" name="Ingresos MXN" stroke="#B85042" strokeWidth={2} dot={{ r: 3 }} />
                    <Line yAxisId="right" type="monotone" dataKey="orders" name="Pedidos" stroke="#0F2922" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              {series.some(s => s.revenue_usd > 0) && (
                <div className="h-48 mt-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={series} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E3D8" />
                      <XAxis dataKey="date" stroke="#6B6660" fontSize={11} tickFormatter={(d) => d.slice(5)} />
                      <YAxis stroke="#10b981" fontSize={11} />
                      <Tooltip contentStyle={{ background: '#FAF9F6', border: '1px solid #E5E3D8', borderRadius: '8px' }} />
                      <Bar dataKey="revenue_usd" name="Ingresos USD" fill="#10b981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Fulfillment breakdown */}
      <Card className="border-[#E5E3D8]" data-testid="card-fulfillment">
        <CardContent className="p-5">
          <h3 className="text-lg font-semibold text-[#0F2922] mb-4">Estado de pedidos</h3>
          {data.fulfillment.length === 0 ? (
            <p className="text-sm text-[#6B6660]">Sin datos aún.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {data.fulfillment.map(f => (
                <span
                  key={f.status}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium ${STATUS_COLORS[f.status] || 'bg-gray-100 text-gray-700'}`}
                  data-testid={`fulfillment-${f.status}`}
                >
                  {STATUS_LABELS[f.status] || f.status}: {f.count}
                </span>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top items */}
      <Card className="border-[#E5E3D8]" data-testid="card-top-items">
        <CardContent className="p-5">
          <h3 className="text-lg font-semibold text-[#0F2922] mb-4">Top platillos vendidos</h3>
          {data.top_items.length === 0 ? (
            <p className="text-sm text-[#6B6660]">Sin ventas registradas aún.</p>
          ) : (
            <div className="space-y-3">
              {data.top_items.map((it, idx) => {
                const max = data.top_items[0]?.quantity || 1;
                const pct = (it.quantity / max) * 100;
                return (
                  <div key={it.name} data-testid={`top-item-${idx}`}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium text-[#0F2922]">{it.name}</span>
                      <span className="text-[#6B6660]">{it.quantity} uds · ${it.revenue_mxn.toLocaleString('es-MX')} MXN</span>
                    </div>
                    <div className="h-2 bg-[#FAF9F6] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#B85042] to-[#D87060] rounded-full transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-[#6B6660] text-right" data-testid="dashboard-refreshed-at">
        Actualizado: {new Date(data.generated_at).toLocaleTimeString('es-MX')} · se refresca cada 30s
      </p>
    </div>
  );
};

export default Dashboard;
